import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { getPostBySlug, getRelatedPosts } from '../data/blogPosts';
import { Clock, ArrowLeft, ArrowRight, Phone, Calendar } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const relatedPosts = getRelatedPosts(slug, 3);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <main data-testid="blog-post-page" className="bg-gray-50 min-h-screen">
      <SEO 
        title={`${post.title} | BBM Appliances Blog`}
        description={post.metaDescription}
        canonical={`https://www.bbmappliances.com/blog/${post.slug}`}
      />
      
      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 lg:h-96 bg-foreground">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/blog" 
              data-testid="back-to-blog"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            <span className="inline-block px-3 py-1 bg-primary text-white text-sm font-medium rounded-full mb-4">
              {post.category}
            </span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-white/70 text-sm">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Article Content */}
        <article 
          data-testid="blog-content"
          className="bg-white rounded-2xl shadow-sm p-6 sm:p-10 lg:p-12 prose prose-lg max-w-none
            prose-headings:font-heading prose-headings:text-foreground
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-gray-600 prose-p:leading-relaxed
            prose-li:text-gray-600
            prose-ul:my-4 prose-ol:my-4
            prose-strong:text-foreground"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* CTA Box */}
        <div 
          data-testid="post-cta"
          className="mt-10 bg-gradient-to-r from-primary to-blue-600 rounded-2xl p-8 text-center"
        >
          <h3 className="text-xl sm:text-2xl font-heading font-bold text-white mb-3">
            Need Appliance Repair?
          </h3>
          <p className="text-white/80 mb-6">
            Our professional technicians are ready to help. Same-day service available in PA & NJ.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/book"
              data-testid="post-cta-book"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary font-semibold rounded-full hover:bg-gray-100 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Book Online
            </Link>
            <a 
              href="tel:+18556056034"
              data-testid="post-cta-call"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-colors"
            >
              <Phone className="w-5 h-5" />
              (855) 605-6034
            </a>
          </div>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section data-testid="related-posts" className="mt-16">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-8">
              Related Articles
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  data-testid={`related-${relatedPost.slug}`}
                  className="group bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-gray-500">{relatedPost.category}</span>
                    <h3 className="text-sm font-semibold text-foreground mt-1 group-hover:text-primary transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-xs text-primary mt-3 group-hover:gap-2 transition-all">
                      Read more <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default BlogPostPage;
